import { AxiosError } from 'axios'
import { API, unwrapData } from '../axios-instance'
import {
  Batch,
  BatchActionsConfig,
  BatchConfig,
  BatchingRoutes,
  BatchItem,
  BatchResult,
  BatchRouteActions,
  BatchRoutes,
  BatchStepEnum,
} from './batch.types'
import { BatchNotFoundError } from './errors'
import { PageResult } from './types'

const handleBatchErrors = (error: AxiosError) => {
  if (error.response?.status === 404) {
    throw new BatchNotFoundError()
  }
  throw error
}
/**
 * Responsible for batching/bulk requests
 * @deprecated Use the {@link createBatchingRoutes} method instead.
 */
export const createBatchRoutes = <FiltersT, ItemT, ParamsT, ErrorsT, BodyT = object>(
  basePath: string,
): BatchRoutes<FiltersT, ItemT, ParamsT, ErrorsT, BodyT> => {
  return {
    init: (body, filters) => API.post<Batch>(basePath, body, { params: filters }).then(unwrapData),
    preview: (batchId, pagination: { page: number; pageSize: number }) =>
      API.get<PageResult<BatchItem<ItemT, ParamsT, ErrorsT>>>(`${basePath}/${batchId}`, {
        params: pagination,
      })
        .then(unwrapData)
        .catch(handleBatchErrors),
    update: (batchId, actions) =>
      API.put<true>(`${basePath}/${batchId}`, actions).then(unwrapData).catch(handleBatchErrors),
    execute: (batchId, body) =>
      API.post<BatchResult>(`${basePath}/${batchId}`, body)
        .then(unwrapData)
        .catch(handleBatchErrors),
  }
}

export const createBatchingRoutes = <Actions extends Partial<BatchRouteActions>>(
  config: BatchConfig,
): BatchingRoutes<Actions> => {
  type ResolvedActions = BatchActionsConfig<Actions>
  const additionalPath = (batchStep: BatchStepEnum) => {
    return config[batchStep]?.path ? `/${config[batchStep]?.path}` : ''
  }

  return {
    init: async (
      body?: ResolvedActions['init']['body'],
      params?: ResolvedActions['init']['params'],
    ) => {
      return await API.post<ResolvedActions['init']['result']>(
        `${config.baseUrl}${additionalPath(BatchStepEnum.PREVIEW)}`,
        body,
        {
          params,
          headers: config[BatchStepEnum.INITIATE]?.headers,
        },
      )
        .then(unwrapData)
        .catch(handleBatchErrors)
    },
    update: async (
      batchId: string,
      body?: ResolvedActions['update']['body'],
      params?: ResolvedActions['update']['params'],
    ) => {
      const url = `${config.baseUrl}/${batchId}${additionalPath(BatchStepEnum.UPDATE)}`
      return await API.post<ResolvedActions['update']['result']>(url, body, {
        params,
        headers: config[BatchStepEnum.UPDATE]?.headers,
      })
        .then(unwrapData)
        .catch(handleBatchErrors)
    },
    preview: async (batchId: string, body?: ResolvedActions['preview']['body']) => {
      const url = `${config.baseUrl}/${batchId}${additionalPath(BatchStepEnum.PREVIEW)}`
      if (body) {
        return await API.post<ResolvedActions['preview']['result']>(url, body, {
          headers: config[BatchStepEnum.PREVIEW]?.headers,
          params: {
            preview: true,
          },
        })
          .then(unwrapData)
          .catch(handleBatchErrors)
      }
      return await API.get<ResolvedActions['preview']['result']>(url, {
        headers: config[BatchStepEnum.PREVIEW]?.headers,
      })
        .then(unwrapData)
        .catch(handleBatchErrors)
    },
    execute: async (batchId: string, body?: ResolvedActions['execute']['body']) =>
      API.post<ResolvedActions['execute']['result']>(
        `${config.baseUrl}/${batchId}${additionalPath(BatchStepEnum.EXECUTE)}`,
        body,
      )
        .then(unwrapData)
        .catch(handleBatchErrors),
  }
}
